<template>
  <!-- Fullscreen overlay above the app -->
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
    <div class="w-full max-w-md">
      <!-- Card -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 space-y-6">
        <!-- Logo/Header -->
        <div class="text-center">
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Frisse Blikken Sourcing</h1>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Login met je Frisse Blikken account
          </p>
        </div>

        <!-- Google Login Button -->
        <div class="space-y-4">
          <div class="flex justify-center">
            <GoogleLogin :callback="handleLogin" :client-id="clientId">
              <template #default="{ onClick }">
                <button
                  @click="onClick"
                  :disabled="isLoading"
                  type="button"
                  class="flex justify-center items-center gap-3 py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg class="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span v-if="!isLoading">Log in met Google</span>
                  <span v-else>Bezig met inloggen...</span>
                </button>
              </template>
            </GoogleLogin>
          </div>

          <!-- Error Message -->
          <div
            v-if="error"
            class="rounded-lg bg-red-50 dark:bg-red-900/30 p-4 border border-red-200 dark:border-red-800"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <svg
                  class="h-5 w-5 text-red-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800 dark:text-red-300">Inloggen mislukt</h3>
                <div class="mt-2 text-sm text-red-700 dark:text-red-400">
                  {{ error }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="text-center text-xs text-gray-500 dark:text-gray-400">
          Vragen? Neem contact op met het Sourcing team of Enable
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { GoogleLogin } from 'vue3-google-login'
import { useAuth } from '@/composables/auth/useAuth'

const router = useRouter()
const route = useRoute()
const { login, isLoading, error: _authError, isAuthorized } = useAuth()
const clientId = import.meta.env.VITE_OAUTH_CLIENT_ID
const error = ref(null)

// Check for authorization error from redirect
onMounted(() => {
  if (route.query.error === 'not_authorized') {
    error.value =
      'Je account is niet geautoriseerd om deze applicatie te gebruiken. Neem contact op met Enable.'
  }
})

async function handleLogin(response) {
  error.value = null

  try {
    if (!response?.credential && !response?.code) {
      throw new Error('No credential or code received from Google. Check OAuth configuration.')
    }

    // Pass the entire response object - useAuth will handle both flows
    await login(response)

    // Check if user is authorized
    if (!isAuthorized.value) {
      error.value =
        'Je account is niet geautoriseerd om deze applicatie te gebruiken. Neem contact op met Enable.'
      return
    }

    // Follow the redirect query set by the auth guard (internal paths only)
    const redirect = route.query.redirect
    const isInternalPath =
      typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
    router.push(isInternalPath ? redirect : '/')
  } catch (err) {
    console.error('Login error:', err)
    error.value = err.message || 'Failed to login. Please try again.'
  }
}
</script>

<style scoped>
/* Add any custom styles here if needed */
</style>
